import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Check, ChevronDown, ChevronUp, Pencil, Pizza, Plus, SlidersHorizontal, Sun, Trash2, X } from 'lucide-react';
import { api, money } from '../../lib/api';
import { useBase, useScope } from '../../scope';
import { Switch } from './bits';
import { TYPE_LABEL, byRank, errMsg, priceOf, type Category, type Item } from './types';
import './menu.css';

/** The catalog manager: categories as sections, items within. */
export function MenuList() {
  const { rid, sid } = useScope();
  const base = useBase();
  const nav = useNavigate();
  const ui = `/r/${rid}/s/${sid}/menu`;

  const [cats, setCats] = useState<Category[] | null>(null);
  const [items, setItems] = useState<Item[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // inline category editing
  const [newCatName, setNewCatName] = useState('');
  const [editingCat, setEditingCat] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const load = useCallback(async () => {
    try {
      const [c, i] = await Promise.all([
        api.get<Category[]>(`${base}/categories`),
        api.get<Item[]>(`${base}/items`),
      ]);
      setCats(c.sort(byRank));
      setItems(i.sort(byRank));
      setError(null);
    } catch (e) {
      setError(errMsg(e, 'Failed to load the menu.'));
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      await load();
      setError(null);
    } catch (e) {
      setError(errMsg(e, 'That didn’t work.'));
    } finally {
      setBusy(false);
    }
  }

  const createCategory = () => {
    const name = newCatName.trim();
    if (!name) return;
    void act(async () => {
      await api.post(`${base}/categories`, { name });
      setNewCatName('');
    });
  };

  const renameCategory = (c: Category) => {
    const name = editName.trim();
    if (!name) return;
    void act(async () => {
      await api.put(`${base}/categories/${c.categoryId}`, {
        name,
        description: c.description,
        imageUrl: c.imageUrl,
        backgroundColor: c.backgroundColor,
      });
      setEditingCat(null);
    });
  };

  const toggleAvailable = (it: Item) =>
    void act(() =>
      api.put(`${base}/items/${it.itemId}/availability`, {
        available: !it.available,
        availablePickup: it.availablePickup,
        availableDelivery: it.availableDelivery,
        availableDineIn: it.availableDineIn,
      }),
    );

  const priceLabel = (it: Item): string => {
    const p = priceOf(it);
    if (p) return money(p.priceInCents);
    if (it.itemType === 'pizza' || it.itemType === 'half_and_half') return 'sized';
    return 'sum of picks';
  };

  const itemsOf = (categoryId: string | null) =>
    (items ?? []).filter((i) => i.categoryId === categoryId);
  const uncategorised = itemsOf(null);

  const itemRows = (list: Item[]) => (
    <>
      {list.length === 0 && <div className="empty">No items yet.</div>}
      {list.map((it, idx) => (
        <div key={it.itemId} className="rowline">
          <div className="grow" style={{ cursor: 'pointer' }} role="link" aria-label={`Edit ${it.name}`} onClick={() => nav(`${ui}/items/${it.itemId}`)}>
            <b>{it.name}</b>
            <span className="sub">{it.description ?? ''}</span>
          </div>
          <span className="src">{TYPE_LABEL[it.itemType] ?? it.itemType}</span>
          {it.soldOut && <span className="chip chip-sold_out">sold out</span>}
          <span className="money" style={{ fontWeight: 700, minWidth: 64, textAlign: 'right' }}>
            {priceLabel(it)}
          </span>
          <button className="mn-iconbtn" title="Move up" disabled={busy || idx === 0}
            onClick={() => void act(() => api.post(`${base}/items/${it.itemId}/move-up`))}>
            <ChevronUp size={16} />
          </button>
          <button className="mn-iconbtn" title="Move down" disabled={busy || idx === list.length - 1}
            onClick={() => void act(() => api.post(`${base}/items/${it.itemId}/move-down`))}>
            <ChevronDown size={16} />
          </button>
          <Switch on={it.available} disabled={busy} title={it.available ? 'Available — tap to hide' : 'Hidden — tap to make available'}
            onToggle={() => toggleAvailable(it)} />
        </div>
      ))}
    </>
  );

  return (
    <>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <div>
          <div className="overline">Store · Menu</div>
          <h1>Menu</h1>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Link className="btn btn-outline btn-sm" to={`${ui}/pizza-settings`}>
            <Pizza size={15} /> Pizza settings
          </Link>
          <Link className="btn btn-outline btn-sm" to={`${ui}/modifiers`}>
            <SlidersHorizontal size={15} /> Modifier groups
          </Link>
          <Link className="btn btn-outline btn-sm" to={`${ui}/sold-out`}>
            <Sun size={15} /> Sold out today
          </Link>
        </div>
      </div>
      {error && <div className="err">{error}</div>}
      {(cats === null || items === null) && <div className="loading">Loading…</div>}

      {cats?.map((c, idx) => (
        <section key={c.categoryId} className="mn-cat">
          <div className="mn-cat-head">
            {editingCat === c.categoryId ? (
              <div className="mn-inline-form" style={{ flex: 1 }}>
                <input className="input" value={editName} autoFocus
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') renameCategory(c); if (e.key === 'Escape') setEditingCat(null); }} />
                <button className="btn btn-primary btn-sm" disabled={busy || !editName.trim()} onClick={() => renameCategory(c)}>
                  <Check size={14} /> Save
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditingCat(null)}><X size={14} /></button>
              </div>
            ) : (
              <>
                <h2>{c.name}</h2>
                <span className="mn-count">{itemsOf(c.categoryId).length} items</span>
                <button className="mn-iconbtn" title="Rename category" disabled={busy}
                  onClick={() => { setEditingCat(c.categoryId); setEditName(c.name); }}>
                  <Pencil size={14} />
                </button>
              </>
            )}
            <span style={{ flex: 1 }} />
            <button className="mn-iconbtn" title="Move category up" disabled={busy || idx === 0}
              onClick={() => void act(() => api.post(`${base}/categories/${c.categoryId}/move-up`))}>
              <ChevronUp size={16} />
            </button>
            <button className="mn-iconbtn" title="Move category down" disabled={busy || idx === cats.length - 1}
              onClick={() => void act(() => api.post(`${base}/categories/${c.categoryId}/move-down`))}>
              <ChevronDown size={16} />
            </button>
            <button className="mn-iconbtn danger" title="Delete category" disabled={busy}
              onClick={() => {
                if (confirm(`Delete category “${c.name}”? Only empty categories can be deleted.`))
                  void act(() => api.del(`${base}/categories/${c.categoryId}`));
              }}>
              <Trash2 size={15} />
            </button>
            <Link className="btn btn-outline btn-sm" to={`${ui}/items/new?categoryId=${c.categoryId}`}>
              <Plus size={14} /> Add item
            </Link>
          </div>
          <div className="mn-cat-body">{itemRows(itemsOf(c.categoryId))}</div>
        </section>
      ))}

      {uncategorised.length > 0 && (
        <section className="mn-cat">
          <div className="mn-cat-head">
            <h2>Uncategorised</h2>
            <span className="mn-count">{uncategorised.length} items</span>
            <span style={{ flex: 1 }} />
            <Link className="btn btn-outline btn-sm" to={`${ui}/items/new`}>
              <Plus size={14} /> Add item
            </Link>
          </div>
          <div className="mn-cat-body">{itemRows(uncategorised)}</div>
        </section>
      )}

      {cats !== null && (
        <div className="mn-inline-form" style={{ maxWidth: 420, marginTop: 6 }}>
          <input className="input" placeholder="New category name…" aria-label="New category name" value={newCatName}
            onChange={(e) => setNewCatName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') createCategory(); }} />
          <button className="btn btn-primary btn-sm" disabled={busy || !newCatName.trim()} onClick={createCategory}>
            <Plus size={14} /> Add category
          </button>
        </div>
      )}
    </>
  );
}
